import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { parseIso } from "@/lib/dates";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldLabel } from "@/components/FieldLabel";
import { hoursBetween, hhmm } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import MultiSelect from "@/components/MultiSelect";
import ComboBox from "@/components/ComboBox";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import { useSearch } from "@/lib/search";
import EntityPicker from "@/components/EntityPicker";
import { FieldError, invalidClass } from "@/components/FieldError";
import { useFieldErrors } from "@/lib/formErrors";

const EMPLOYEE_FIELDS = ["full_name", "employee_code", "designation"];

// The boxes this form draws, by the name the server calls them. An error about any
// other field has nowhere to sit and goes to a toast instead of vanishing.
const FORM_FIELDS = ["title", "unit_id", "training_date", "start_time", "end_time", "duration_hours",
                     "estimated_cost", "actual_cost", "trainer_id", "trainer_employee_id",
                     "trainer_name", "training_type", "category"];

// A legacy internal trainer record with no employee behind it is offered under this
// prefix so one picker can hold both kinds of id.
const RECORD = "trainer:";

// A Select item cannot carry an empty value, so the "new" row needs a sentinel that no
// real category could collide with.
const NEW_CATEGORY = "__new_category__";

// An outside agency engaged for one session should not have to become a permanent master
// record first. An internal trainer is a colleague and belongs in Masters, where they can
// be linked to their employee record and picked again — so this escape is external-only.
const ONE_OFF_TRAINER = "__one_off_trainer__";

export default function TrainingForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = id && id !== "new";
  // The calendar opens this form on a day (`?date=`) and, when it was filtered, on a
  // unit (`?unit_id=`), so what was clicked is already filled in. Only a well-formed
  // date is taken; anything else is as good as no date.
  const [sp] = useSearchParams();
  const presetDate = !editing && parseIso(sp.get("date")) ? sp.get("date") : "";
  const presetUnit = (!editing && sp.get("unit_id")) || "";
  const [f, setF] = useState({
    title: "", description: "", justification: "",
    training_type: "INTERNAL", category: "Safety",
    trainer_id: "", trainer_employee_id: "", trainer_name: "", trainer_organization: "",
    // unit_id is the one a certificate names; unit_ids is every unit it is held for and
    // always contains unit_id. The server enforces that, and so does `setUnits` below.
    unit_id: presetUnit, unit_ids: presetUnit ? [presetUnit] : [], department_ids: [],
    venue: "", training_date: presetDate, start_time: "10:00", end_time: "16:00",
    duration_hours: 4, mode: "Classroom", estimated_cost: 0, actual_cost: 0,
    participant_ids: [],
  });
  const [units, setUnitList] = useState([]);
  const [depts, setDepts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [categories, setCategories] = useState([]);
  // null means "show the people this training is actually for". Touching either picker
  // filter pins it, so narrowing the list by hand is not undone by editing the scope.
  const [pickerScope, setPickerScope] = useState(null);
  const [empQ, setEmpQ] = useState("");
  // What has been typed into these boxes before. Suggestions only — a training in a room
  // nobody has used yet still has to be enterable.
  const [hints, setHints] = useState({ venues: [], organizations: [] });
  // External only: the caller said the trainer is not one of the records.
  const [oneOff, setOneOff] = useState(false);
  // A category typed here is saved to the shared list, not onto this one training — so it
  // exists for the next request, the filters, and the certificate templates too.
  const [newCategory, setNewCategory] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const { errors, report, clear, fieldProps } = useFieldErrors();

  useEffect(() => {
    (async () => {
      const [u, d, e, tr, c, s] = await Promise.all([
        api.get("/units"), api.get("/departments"), api.get("/employees"),
        api.get("/trainers"), api.get("/categories"), api.get("/suggestions"),
      ]);
      setUnitList(u.data); setDepts(d.data); setEmps(e.data); setTrainers(tr.data); setCategories(c.data);
      setHints(s.data);
      if (editing) {
        const t = await api.get(`/trainings/${id}`);
        // A training saved with a typed-in trainer and no record behind it opens on the
        // one-off fields, or the name somebody entered would appear to have vanished.
        if (!t.data.trainer_id && (t.data.trainer_name || "").trim()) setOneOff(true);
        setF({
          ...t.data,
          // The picker shows the colleague, not the trainer record standing for them.
          trainer_employee_id: t.data.trainer_employee_id || "",
          // A training saved before the scope existed answers with one unit; the server
          // backfilled the rest, but a null here would put the form into an odd state
          // that only shows up when somebody presses save.
          unit_ids: t.data.unit_ids?.length ? t.data.unit_ids : [t.data.unit_id].filter(Boolean),
          department_ids: t.data.department_ids || [],
          participant_ids: (t.data.participants || []).map((p) => p.employee_id),
        });
      }
    })();
  }, [id]);

  // Duration follows the clock. It stays editable — a session with a break is not the
  // difference between its start and end — but nobody has to work it out by hand.
  const set = (k, v) => setF((s) => {
    clear(k);
    const next = { ...s, [k]: v };
    if (k === "start_time" || k === "end_time") {
      const hrs = hoursBetween(next.start_time, next.end_time);
      if (hrs !== null) next.duration_hours = hrs;
    }
    return next;
  });

  // The certificate has to name a unit, so the primary can never fall outside the set:
  // removing the unit that was primary promotes the first one left rather than leaving
  // the form pointing at a unit it no longer claims to cover.
  const setUnits = (ids) => { clear("unit_id"); setF((s) => ({
    ...s,
    unit_ids: ids,
    unit_id: ids.includes(s.unit_id) ? s.unit_id : (ids[0] || ""),
    // A department belongs to exactly one unit. Dropping a unit drops its departments,
    // or the training keeps claiming a department in a plant it is no longer held at.
    department_ids: s.department_ids.filter(
      (did) => ids.includes(depts.find((d) => d.id === did)?.unit_id)),
  })); };

  const toggleEmp = (eid) => setF((s) => ({
    ...s,
    participant_ids: s.participant_ids.includes(eid) ? s.participant_ids.filter((x) => x !== eid) : [...s.participant_ids, eid],
  }));

  // The trainers of the kind this training is. Switching Internal/External changes which
  // list applies, so a trainer left selected from the other list is cleared — otherwise
  // an external training keeps naming a colleague who is no longer on its dropdown.
  const wantedType = f.training_type === "INTERNAL" ? "internal" : "external";
  const trainerOptions = useMemo(
    () => trainers.filter((t) => t.type === wantedType), [trainers, wantedType]);
  const chosenTrainer = trainers.find((t) => t.id === f.trainer_id) || null;

  // An internal trainer is a colleague, so the internal picker is the employee register
  // — searched by name, code or designation, and what the record knows fills in below.
  // Internal trainer rows made in Masters without an employee behind them still appear,
  // under their own prefix, so a training saved against one can be reopened.
  const colleagueOptions = useMemo(() => [
    ...emps.map((e) => ({
      id: e.id, name: e.full_name,
      hint: [e.employee_code, e.designation, depts.find((d) => d.id === e.department_id)?.name].filter(Boolean).join(" · "),
      keywords: e.email,
    })),
    ...trainers.filter((t) => t.type === "internal" && !t.employee_id).map((t) => ({
      id: RECORD + t.id, name: t.name,
      hint: ["Trainer record", t.expertise_area, t.organization].filter(Boolean).join(" · "),
    })),
  ], [emps, depts, trainers]);
  const colleagueValue = f.trainer_employee_id || (f.trainer_id && !chosenTrainer?.employee_id ? RECORD + f.trainer_id : "");
  const chosenColleague = f.trainer_employee_id ? emps.find((e) => e.id === f.trainer_employee_id) : null;
  const pickColleague = (id) => {
    clear("trainer_employee_id"); clear("trainer_id");
    setF((s) => id.startsWith(RECORD)
      ? { ...s, trainer_id: id.slice(RECORD.length), trainer_employee_id: "", trainer_name: "", trainer_organization: "" }
      : { ...s, trainer_id: "", trainer_employee_id: id, trainer_name: "", trainer_organization: "" });
  };

  const setTrainingType = (training_type) => {
    const nextType = training_type === "INTERNAL" ? "internal" : "external";
    setF((s) => {
      const kept = trainers.find((t) => t.id === s.trainer_id);
      const stillFits = kept && kept.type === nextType;
      return {
        ...s, training_type,
        trainer_id: stillFits ? s.trainer_id : "",
        trainer_employee_id: nextType === "internal" ? s.trainer_employee_id : "",
        trainer_name: stillFits ? s.trainer_name : "",
        trainer_organization: stillFits ? s.trainer_organization : "",
      };
    });
    if (training_type === "INTERNAL") setOneOff(false);
  };

  // Picking a record fills in what the record already knows. The organization is copied
  // onto the training rather than only linked, because it is a stored column that reports
  // and the certificate template read — and it stays editable, since an agency can send
  // somebody under a different banner.
  const pickTrainer = (id) => {
    if (id === ONE_OFF_TRAINER) {
      setOneOff(true);
      setF((s) => ({ ...s, trainer_id: "", trainer_name: "", trainer_organization: "" }));
      return;
    }
    const t = trainers.find((x) => x.id === id);
    setOneOff(false);
    clear("trainer_id");
    setF((s) => ({
      ...s,
      trainer_id: id,
      trainer_employee_id: "",
      trainer_name: "",
      trainer_organization: t?.organization || "",
    }));
  };

  // Only departments of the units this training covers — plus any already saved against
  // it, so a department does not vanish from the form the moment its unit is edited.
  const deptOptions = useMemo(() => depts.filter(
    (d) => f.unit_ids.includes(d.unit_id) || f.department_ids.includes(d.id)
  ).map((d) => ({ id: d.id, name: d.name, hint: units.find((u) => u.id === d.unit_id)?.name })),
  [depts, units, f.unit_ids, f.department_ids]);

  const pickerUnits = pickerScope ? pickerScope.units : f.unit_ids;
  const pickerDepts = pickerScope ? pickerScope.depts : f.department_ids;
  const pinPicker = (patch) => setPickerScope({ units: pickerUnits, depts: pickerDepts, ...patch });

  const scopedEmps = useMemo(() => emps.filter(
    (e) => (pickerUnits.length === 0 || pickerUnits.includes(e.unit_id))
        && (pickerDepts.length === 0 || pickerDepts.includes(e.department_id))
  ), [emps, pickerUnits, pickerDepts]);
  // This was the only list that never trimmed its query, so a code pasted out of a
  // spreadsheet matched nothing here while matching fine on the employees screen. It
  // also re-lowercased the query once per row.
  const filteredEmps = useSearch(scopedEmps, empQ, EMPLOYEE_FIELDS);

  const selectAllFiltered = () => setF((s) => {
    const ids = new Set(s.participant_ids);
    filteredEmps.forEach((e) => ids.add(e.id));
    return { ...s, participant_ids: Array.from(ids) };
  });

  // Saved to the shared list through the endpoint Settings already uses, so there is one
  // source of categories rather than a second, per-training spelling of them.
  const addCategory = async () => {
    const name = (newCategory || "").trim();
    if (!name) return;
    // Settings edits this same list as one comma-separated box, so a comma inside a name
    // splits it into several the next time anybody saves there — and the trainings
    // pointing at the original then match nothing.
    if (name.includes(",")) {
      toast.error("A category cannot contain a comma — commas separate them on the Settings screen");
      return;
    }
    const clash = categories.find((c) => c.toLowerCase() === name.toLowerCase());
    if (clash) {
      set("category", clash);
      setNewCategory(null);
      toast.info(`"${clash}" already exists — selected it`);
      return;
    }
    setSavingCategory(true);
    try {
      const next = [...categories, name];
      await api.put("/categories", { categories: next });
      setCategories(next);
      set("category", name);
      setNewCategory(null);
      toast.success(`Category "${name}" added`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not add the category"));
    }
    setSavingCategory(false);
  };

  const badTimes = !!f.start_time && !!f.end_time && hoursBetween(f.start_time, f.end_time) === null;

  const submit = async () => {
    // Checked here so the person filling the form is told which box is wrong, under
    // that box, rather than finding out from a round trip. The server checks the same
    // things again and its answers land in the same places.
    const problems = {};
    if (!f.title?.trim()) problems.title = "Title is required";
    if (f.unit_ids.length === 0) problems.unit_id = "Pick at least one unit";
    // Nothing checked this, so a training could be saved ending before it began — and the
    // duration beside it was then whatever had been typed.
    if (badTimes) problems.end_time = "End time must be after the start time";
    for (const [k, label] of [["duration_hours", "Duration"], ["estimated_cost", "Estimated cost"], ["actual_cost", "Actual cost"]]) {
      if (f[k] !== "" && f[k] != null && Number(f[k]) < 0) problems[k] = `${label} cannot be negative`;
    }
    if (Object.keys(problems).length) { report({ fields: problems }); return; }
    try {
      const payload = { ...f, duration_hours: parseFloat(f.duration_hours) || 0, estimated_cost: parseFloat(f.estimated_cost) || 0, actual_cost: parseFloat(f.actual_cost) || 0 };
      if (editing) {
        await api.put(`/trainings/${id}`, payload);
        toast.success("Training updated");
        nav(`/trainings/${id}`);
      } else {
        const r = await api.post("/trainings", payload);
        toast.success("Training saved as Draft");
        nav(`/trainings/${r.data.id}`);
      }
    } catch (err) {
      report(err, { fallback: "Failed to save", known: FORM_FIELDS });
    }
  };

  const unitOptions = units.map((u) => ({ id: u.id, name: u.name, hint: u.location }));
  const followingScope = pickerScope === null && (f.unit_ids.length > 0 || f.department_ids.length > 0);

  return (
    <div className="space-y-5" data-testid="training-form">
      <Button variant="ghost" onClick={() => nav(-1)} className="pl-0"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div>
        <div className="overline text-primary">Training</div>
        <h2 className="font-heading font-bold text-3xl mt-1">{editing ? "Edit training" : "New training"}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 border border-border lg:col-span-2 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FieldLabel htmlFor="tf-title" required>Title</FieldLabel>
              <Input id="tf-title" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="tf-title"
                     className={invalidClass(errors, "title")} {...fieldProps("title")} />
              <FieldError field="title" errors={errors} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="tf-desc">Description</Label>
              <Textarea id="tf-desc" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="tf-desc" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="tf-just">Business justification</Label>
              <Textarea id="tf-just" value={f.justification} onChange={(e) => set("justification", e.target.value)} data-testid="tf-just" />
            </div>
            <div {...fieldProps("training_type")}>
              <Label>Training type</Label>
              <Select value={f.training_type} onValueChange={setTrainingType}>
                <SelectTrigger data-testid="tf-type" aria-label="Training type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNAL">Internal</SelectItem>
                  <SelectItem value="EXTERNAL">External</SelectItem>
                </SelectContent>
              </Select>
              <FieldError field="training_type" errors={errors} />
            </div>
            <div>
              <Label>Category</Label>
              {newCategory === null ? (
                <Select value={f.category} onValueChange={(v) => v === NEW_CATEGORY ? setNewCategory("") : set("category", v)}>
                  <SelectTrigger data-testid="tf-category" aria-label="Category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value={NEW_CATEGORY} data-testid="tf-category-new">+ New category…</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input autoFocus value={newCategory} placeholder="e.g. Behavioural"
                         onChange={(e) => setNewCategory(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); }
                                             if (e.key === "Escape") setNewCategory(null); }}
                         data-testid="tf-category-input" />
                  <Button type="button" onClick={addCategory} disabled={savingCategory} data-testid="tf-category-save">
                    {savingCategory ? "Adding..." : "Add"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setNewCategory(null)}>Cancel</Button>
                </div>
              )}
            </div>
            <div className="md:col-span-2" {...fieldProps("trainer_id")} data-field="trainer_employee_id">
              <Label htmlFor="trainer-select">
                Trainer ({f.training_type === "INTERNAL" ? "internal — a colleague" : "external"})
              </Label>
              {/* Both kinds are picked from the records. Retyping a name and an agency
                  that Masters already holds is how the same trainer ends up spelled
                  three ways across the register, and none of the three joins up.
                  Internal: the employee register itself, searched by code or name; the
                  server keeps the trainer record behind the person. External: the
                  trainer masters, with a one-off escape for an agency engaged once. */}
              {f.training_type === "INTERNAL" ? (
                <EntityPicker value={colleagueValue} onChange={pickColleague} options={colleagueOptions}
                              placeholder="Search by name, code or designation" noun="colleague"
                              searchPlaceholder="Type a name, employee code or designation..."
                              empty="No employees on record yet — add them under Employees."
                              id="trainer-select" testid="tf-trainer" />
              ) : (
                <Select value={oneOff ? ONE_OFF_TRAINER : (f.trainer_id || "")} onValueChange={pickTrainer}>
                  <SelectTrigger data-testid="tf-trainer" aria-label="Trainer">
                    <SelectValue placeholder="Select trainer" />
                  </SelectTrigger>
                  <SelectContent>
                    {trainerOptions.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.organization ? ` — ${t.organization}` : ""}
                      </SelectItem>
                    ))}
                    {f.training_type === "EXTERNAL" && (
                      <SelectItem value={ONE_OFF_TRAINER} data-testid="tf-trainer-oneoff">
                        + Somebody not on the list…
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}

              {/* What the employee row already knows: filled in, not retyped. */}
              {chosenColleague && (
                <div className="mt-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-0.5"
                     data-testid="tf-colleague-details">
                  <div><span className="text-muted-foreground">Employee code:</span> <span className="font-mono">{chosenColleague.employee_code}</span></div>
                  <div><span className="text-muted-foreground">Designation:</span> {chosenColleague.designation || "—"}</div>
                  <div><span className="text-muted-foreground">Department:</span>{" "}
                    {[depts.find((d) => d.id === chosenColleague.department_id)?.name,
                      units.find((u) => u.id === chosenColleague.unit_id)?.name].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {(chosenColleague.email || chosenColleague.mobile) && (
                    <div><span className="text-muted-foreground">Contact:</span>{" "}
                      {[chosenColleague.email, chosenColleague.mobile].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <div className="text-muted-foreground pt-1">
                    Filled in from the employee record. Certificates name this person as the trainer.
                  </div>
                </div>
              )}
              <FieldError field="trainer_id" errors={errors} />
              <FieldError field="trainer_employee_id" errors={errors} />

              {/* What the record already knows, so nobody has to look it up or retype it. */}
              {chosenTrainer && !oneOff && !chosenColleague && (
                <div className="mt-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs space-y-0.5"
                     data-testid="tf-trainer-details">
                  <div><span className="text-muted-foreground">Organization:</span> {chosenTrainer.organization || "—"}</div>
                  {chosenTrainer.expertise_area && (
                    <div><span className="text-muted-foreground">Expertise:</span> {chosenTrainer.expertise_area}</div>
                  )}
                  {(chosenTrainer.contact_email || chosenTrainer.contact_phone) && (
                    <div><span className="text-muted-foreground">Contact:</span>{" "}
                      {[chosenTrainer.contact_email, chosenTrainer.contact_phone].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <div className="text-muted-foreground pt-1">
                    Filled in from the trainer record. Change it under Masters.
                  </div>
                </div>
              )}

              {/* An agency engaged once. Kept for external only — a colleague belongs in
                  Masters, where they can be linked to their employee record. */}
              {oneOff && (
                <div className="mt-2 grid md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="tf-trainer-name">Trainer name</Label>
                    <Input id="tf-trainer-name" value={f.trainer_name || ""}
                           onChange={(e) => set("trainer_name", e.target.value)} data-testid="tf-trainer-name" />
                  </div>
                  <div>
                    <Label htmlFor="tf-trainer-org">Organization / Agency</Label>
                    <ComboBox value={f.trainer_organization || ""}
                              onChange={(v) => set("trainer_organization", v)}
                              options={hints.organizations || []}
                              placeholder="e.g. Kaizen Institute"
                              id="tf-trainer-org" testid="tf-trainer-org" />
                  </div>
                  <p className="md:col-span-2 text-xs text-muted-foreground">
                    Somebody you will engage again belongs under{" "}
                    <Link to="/masters" className="text-primary underline underline-offset-2">Masters</Link>,
                    so the next training can pick them instead.
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1.5">
                Needed before certificates can be issued. Who delivered a session is part
                of the record an audit asks for, and it is frozen the moment a certificate
                is issued against it.
              </p>
            </div>
            <div {...fieldProps("unit_id")}>
              <FieldLabel required>Units</FieldLabel>
              <MultiSelect
                value={f.unit_ids} onChange={setUnits} options={unitOptions}
                placeholder="Select units" noun="unit" searchPlaceholder="Search units..."
                empty="No units on record yet — add them under Masters."
                className={invalidClass(errors, "unit_id")}
                testid="tf-units" />
              <FieldError field="unit_id" errors={errors} />
              {f.unit_ids.length > 1 ? (
                <div className="mt-2">
                  <Label className="text-xs">Named on certificates</Label>
                  <Select value={f.unit_id || ""} onValueChange={(v) => set("unit_id", v)}>
                    <SelectTrigger data-testid="tf-primary-unit" aria-label="Unit named on certificates">
                      <SelectValue placeholder="Select the unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {f.unit_ids.map((uid) => (
                        <SelectItem key={uid} value={uid}>{units.find((u) => u.id === uid)?.name || uid}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    A certificate names one plant. The register groups this training under it too.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  Pick every unit the training is held for. The first is named on certificates.
                </p>
              )}
            </div>
            <div>
              <Label>Departments</Label>
              <MultiSelect
                value={f.department_ids} onChange={(v) => set("department_ids", v)}
                options={deptOptions} placeholder="All departments" noun="department"
                searchPlaceholder="Search departments..."
                empty={f.unit_ids.length === 0
                  ? "Pick a unit first — departments belong to one."
                  : "The selected units have no departments on record."}
                testid="tf-departments" />
              <p className="text-xs text-muted-foreground mt-1">
                Optional. Left empty the training counts as being for the whole unit.
              </p>
            </div>
            <div>
              <Label htmlFor="tf-venue">Venue</Label>
              <ComboBox value={f.venue} onChange={(v) => set("venue", v)}
                        options={hints.venues || []}
                        placeholder="Training Hall 1"
                        id="tf-venue" testid="tf-venue" />
            </div>
            <div>
              <Label htmlFor="tf-date">Date</Label>
              <Input id="tf-date" type="date" value={f.training_date} onChange={(e) => set("training_date", e.target.value)} data-testid="tf-date"
                     className={invalidClass(errors, "training_date")} {...fieldProps("training_date")} />
              <FieldError field="training_date" errors={errors} />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={f.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="tf-mode" aria-label="Mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Classroom">Classroom</SelectItem>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="On-the-job">On-the-job</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tf-start">Start</Label>
              <Input id="tf-start" type="time" step="60" value={hhmm(f.start_time)} onChange={(e) => { clear("end_time"); set("start_time", e.target.value); }} data-testid="tf-start"
                     {...fieldProps("start_time")} />
              <FieldError field="start_time" errors={errors} />
            </div>
            <div>
              <Label htmlFor="tf-end">End</Label>
              <Input id="tf-end" type="time" step="60" value={hhmm(f.end_time)} onChange={(e) => set("end_time", e.target.value)} data-testid="tf-end"
                     className={invalidClass(errors, "end_time") || (badTimes ? "border-destructive" : "")} {...fieldProps("end_time")} />
              <FieldError field="end_time" errors={errors} />
            </div>
            <div>
              <Label htmlFor="tf-duration">Duration (hrs)</Label>
              <Input id="tf-duration" type="number" min="0" step="0.5" value={f.duration_hours} onChange={(e) => set("duration_hours", e.target.value)} data-testid="tf-duration"
                     className={invalidClass(errors, "duration_hours")} {...fieldProps("duration_hours")} />
              <FieldError field="duration_hours" errors={errors} />
              <p className="text-xs text-muted-foreground mt-1">
                {badTimes ? "End time must be after the start time."
                          : "Filled in from the start and end times. Change it if the session has a break."}
              </p>
            </div>
            <div>
              <Label htmlFor="tf-est-cost">Estimated cost (₹)</Label>
              <Input id="tf-est-cost" type="number" min="0" value={f.estimated_cost} onChange={(e) => set("estimated_cost", e.target.value)} data-testid="tf-est-cost"
                     className={invalidClass(errors, "estimated_cost")} {...fieldProps("estimated_cost")} />
              <FieldError field="estimated_cost" errors={errors} />
            </div>
          </div>
          <Button onClick={submit} data-testid="tf-submit">Save training</Button>
        </Card>

        <Card className="p-6 border border-border">
          <div className="overline text-muted-foreground">Participants</div>
          <h3 className="font-heading font-semibold mt-1 mb-3">Nominate employees ({f.participant_ids.length} selected)</h3>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <MultiSelect
              value={pickerUnits} onChange={(v) => pinPicker({ units: v })} options={unitOptions}
              placeholder="All units" noun="unit" searchPlaceholder="Search units..."
              empty="No units on record yet." testid="emp-filter-unit" />
            <MultiSelect
              value={pickerDepts} onChange={(v) => pinPicker({ depts: v })}
              options={depts.map((d) => ({ id: d.id, name: d.name, hint: units.find((u) => u.id === d.unit_id)?.name }))}
              placeholder="All depts" noun="department" searchPlaceholder="Search departments..."
              empty="No departments on record yet." testid="emp-filter-dept" />
          </div>
          {followingScope ? (
            <p className="text-xs text-muted-foreground mb-2" data-testid="picker-following-scope">
              Showing the units and departments this training is for.{" "}
              <button type="button" className="text-primary underline underline-offset-2"
                      onClick={() => setPickerScope({ units: [], depts: [] })}>Show everyone</button>
            </p>
          ) : pickerScope && (f.unit_ids.length > 0 || f.department_ids.length > 0) ? (
            <p className="text-xs text-muted-foreground mb-2">
              <button type="button" className="text-primary underline underline-offset-2"
                      onClick={() => setPickerScope(null)}>Back to this training's units and departments</button>
            </p>
          ) : null}
          <SearchInput value={empQ} onChange={setEmpQ}
                       placeholder="Search name, code or designation..." testid="emp-search" />
          <Button variant="outline" size="sm" className="mt-2 w-full" onClick={selectAllFiltered} data-testid="select-all-filtered">Select all filtered ({filteredEmps.length})</Button>
          <div className="mt-3 max-h-[440px] overflow-y-auto border border-border rounded-md">
            {filteredEmps.map((e) => (
              <label key={e.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-0 hover:bg-muted cursor-pointer" data-testid={`emp-check-${e.employee_code}`}>
                <Checkbox checked={f.participant_ids.includes(e.id)} onCheckedChange={() => toggleEmp(e.id)} />
                <div className="text-sm">
                  <div className="font-medium">{e.full_name}</div>
                  <div className="text-xs text-muted-foreground">{e.employee_code} · {e.designation}</div>
                </div>
              </label>
            ))}
            {filteredEmps.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                No employees in the units and departments selected above.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
